import React, { useRef, useState, useEffect } from 'react';
import { Copy, Heading } from 'components/components/atoms';
import { Section, Container } from 'components/shared';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import styled from 'styled-components';
import breakpoints from 'utils/breakpoints';
import HeartIcon from 'components/icons/Heart';
import HeartFilledIcon from 'components/icons/HeartFilled';
import useFavorites from 'utils/hooks/useFavorites';
import { useSitecoreContext } from 'stores/sitecoreContext';
import { isValidTitle } from 'utils/validateTitle';
import tw from 'twin.macro';
import cx from 'classnames';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';

const StyledSection = styled(Section)`
  ${tw`focus:outline-none`}
  margin-bottom: 1.563rem;
  padding-top: 3.125rem !important;
  padding-bottom: 3.125rem !important;

  ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
    showNewSchemaColor ? `background-color: ${newSchemaColorHexaCode};` : ''}

  @media ${breakpoints.lg} {
    ${({ withLogo, whiteBackground }) =>
      withLogo && whiteBackground
        ? 'padding-top: 6.25rem!important; padding-bottom: 5rem!important;'
        : ''}
    ${({ withLogo, whiteBackground }) =>
      withLogo && !whiteBackground
        ? 'padding-top: 6.25rem!important; padding-bottom: 6.25rem!important;'
        : ''}
    ${({ withLogo }) =>
      !withLogo
        ? 'padding-top: 5rem!important; padding-bottom: 5rem!important;'
        : ''}      
    ${({ whiteBackground }) =>
      whiteBackground ? 'margin-bottom: 1.563rem;' : 'margin-bottom: 3.125rem;'}
  }
`;

const Img = styled.img`
  ${({ isHorizontal }) =>
    isHorizontal ? 'max-height: 6.25rem;' : 'max-height: 12.5rem;'}
`;

const HeartFilled = styled(HeartFilledIcon)`
  transition: all 0.2s ease-in-out;
  &:hover {
    transform: scale(1.2);
  }
`;

const ProductOverview = ({ fields, rendering }) => {
  const result = getFieldValue(fields, 'result') || {};
  const imgRef = useRef();
  const [isHorizontal, setIsHorizontal] = useState(false);
  const { componentName } = rendering;

  const { newSchemaColorHexaCode, showNewSchemaColor } =
    useNewColorSchemaOptions();
  const whiteBackground = !result.backColor || result.backColor === 'bg-white';
  const withLogo = result.image && result.image.src;

  const showFavorites = result.showFavorites || false;
  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();
  const { ContextID = {} } = useSitecoreContext() || {};
  const id = ContextID.value;
  const isFavorite = favoritesList.includes(id);
  const [heartFilled, setHeartFilled] = useState(false);

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  useEffect(() => {
    if (imgRef.current) {
      const imgHeight = imgRef.current.naturalHeight;
      const imgWidth = imgRef.current.naturalWidth;
      setIsHorizontal(imgWidth > imgHeight);
    }
  }, [imgRef]);

  return (
    <StyledSection
      id={componentName}
      className={showNewSchemaColor ? '' : result.backColor}
      whiteBackground={showNewSchemaColor ? false : whiteBackground}
      withLogo={withLogo}
      tabIndex='-1'
      showNewSchemaColor={showNewSchemaColor}
      newSchemaColorHexaCode={newSchemaColorHexaCode}
    >
      <Container className='flex flex-col items-center'>
        {showFavorites && (
          <button
            className='top-0 right-0 w-1/6 mt-2 h-8 w-8 ml-auto -mr-4 cursor-pointer'
            onClick={() => handleFavoriteClick(id)}
            onMouseEnter={() => setHeartFilled(true)}
            onMouseLeave={() => setHeartFilled(false)}
            aria-label={isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'}
            aria-pressed={isFavorite ? 'true' : 'false'}
            // role="button"
          >
            {isFavorite || heartFilled ? (
              <HeartFilled
                className={cx('mx-2 h-6 w-6', {
                  'text-mattamy-blue': !showNewSchemaColor,
                  'text-black': showNewSchemaColor,
                })}
              />
            ) : (
              <HeartIcon
                className={cx('mx-2', {
                  'text-mattamy-blue': !showNewSchemaColor,
                  'text-black': showNewSchemaColor,
                })}
              />
            )}
          </button>
        )}
        {result.image && result.image.src && (
          <Img
            className='mb-12'
            ref={imgRef}
            isHorizontal={isHorizontal}
            src={result.image.src}
            alt={result.image.alt}
          />
        )}
        {isValidTitle(result.headline) && (
          <Heading
            as='h2'
            color={showNewSchemaColor ? 'black' : 'blue'}
            className='text-4xl leading-10 text-center'
          >
            {result.headline}
          </Heading>
        )}
        <Copy
          color={showNewSchemaColor ? 'black' : 'blue'}
          className='w-9/12 font-graphie text-center font-bold leading-7'
        >
          {result.copy}
        </Copy>
      </Container>
    </StyledSection>
  );
};

export default ProductOverview;
